import { ChangeEvent, useEffect, useMemo, useState } from "react";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Checkbox from "@shared/v2/Checkbox";
import Modal from "@shared/v2/Modal";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";
import { TriangleExclamationSolidV6 } from "@shared/v2/Icomoon";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Loading from "../../../../Goals/components/Loader";

import { TrackingNumber } from "../../types";
import { AccountProduct, Product } from "../../../../Support/Account/AddOnsSupport/types";
import Step1 from "./Step1";
import Step2Up from "./Step2Up";
import ChangeNumberList from "./ChangeNumberList";

interface PhoneSystemChangeModalProps {
  accountProduct?: AccountProduct;
  loadingTrackingNumbers?: boolean;
  trackingNumbers?: TrackingNumber[];
  newProduct?: Product;
  onClose: () => void;
  onChangePlan?: (accountProductId: number, newProductId: number) => Promise<void>;
  open: boolean;
}

const getTitle = ({
  newProduct,
  next,
  step,
}: {
  newProduct?: Product;
  next: "up" | "down";
  step: number;
}) => {
  if (step === 1) return `Confirm ${next === "up" ? "Upgrade" : "Downgrade"}`;
  if (step === 2) {
    return next === "down" ? "Select which tracking numbers to keep" : "";
  }
  if (step === 3) return "Delete tracking numbers?";
  return next === "up" ? `You're on the ${newProduct?.tier} plan now!` : "Successfully downgraded";
};

const getDescription = ({
  newProduct,
  next,
  step,
  trackingNumbers,
}: {
  newProduct?: Product;
  next: "up" | "down";
  step: number;
  trackingNumbers?: TrackingNumber[];
}) => {
  if (step === 2 && next === "up") {
    return "Congrats and thanks for upgrading. You now have access to:";
  }
  if (step === 2 && next === "down") {
    return (
      <div>
        You currently have <b>{trackingNumbers?.length}</b> tracking numbers, but the plan you’re switching to
        only has <b>{newProduct?.details.trackingNumberMax}</b> included numbers. Select which numbers you
        want to keep - the others will be deleted when your new plan takes into effect.
      </div>
    );
  }
  if (step === 3) {
    return (
      <div>
        <b>Confirm the tracking numbers you wish to keep and the numbers you wish to delete.</b> You and your
        team will still be able to access timeline events and activity feed notifications for deleted numbers.
        We will maintain call-recordings and transcriptions up to 90 days. After 90 days, they may be removed.
      </div>
    );
  }
  return "";
};

const PhoneSystemChangeModal: React.FC<PhoneSystemChangeModalProps> = ({
  accountProduct,
  loadingTrackingNumbers = false,
  newProduct,
  onClose,
  onChangePlan,
  open,
  trackingNumbers,
}) => {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [keepNumbers, setKeepNumbers] = useState<TrackingNumber[]>([]);
  const [loading, setLoading] = useState(false);
  const activeTrackingNumberCount = trackingNumbers?.length || 0;
  const newPlanTrackingNumberMax = newProduct?.details.trackingNumberMax || 0;
  const currentPlanTrackingNumberMax = accountProduct?.product.details.trackingNumberMax || 0;
  const requiresNumberDeletion = activeTrackingNumberCount > newPlanTrackingNumberMax;
  const deleteNumbers = useMemo(
    () =>
      requiresNumberDeletion
        ? trackingNumbers?.filter((tn) => !keepNumbers.find((n) => n.id === tn.id)) || []
        : [],
    [keepNumbers, trackingNumbers, requiresNumberDeletion],
  );

  useEffect(() => {
    if (!open) setStep(1);
  }, [open]);

  const handleDeleteNumbers = async () => {
    if (!requiresNumberDeletion || deleteNumbers.length === 0) {
      return true;
    }

    try {
      for (const number of deleteNumbers) {
        await caseTransformingAxios.delete(`/phone_system/tracking_numbers/${number.id}`);
      }
      return true;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : "Failed to delete tracking numbers");
    }
  };

  const handleChangeSubscription = async () => {
    if (!accountProduct?.id || !newProduct?.id || !onChangePlan) {
      setErrors({ global: "Missing required information for subscription change." });
      return;
    }

    try {
      await onChangePlan(Number(accountProduct.id), Number(newProduct.id));
      setErrors({});
      setStep(4);
    } catch (error) {
      setErrors({
        global:
          error instanceof Error
            ? error.message
            : "An error occurred while updating your subscription. Please try again or contact support.",
      });
    }
  };

  const handleDowngradeWithDeletion = async () => {
    setLoading(true);
    try {
      if (requiresNumberDeletion && deleteNumbers.length > 0) {
        await handleDeleteNumbers();
      }
      await handleChangeSubscription();
    } catch (error) {
      setErrors({
        global: error instanceof Error ? error.message : "An error occurred while processing your downgrade.",
      });
    } finally {
      setLoading(false);
    }
  };

  const next = newPlanTrackingNumberMax >= currentPlanTrackingNumberMax ? "up" : "down";

  return (
    <Modal
      onHide={onClose}
      show={open}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[800px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px] tw-overflow-hidden"
    >
      {/* @ts-expect-error props */}
      <Modal.Header
        title={getTitle({ newProduct, next, step })}
        description={getDescription({ newProduct, next, step, trackingNumbers })}
        closeButton
      />

      <Modal.Body className="tw-flex-1 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-[24px] tw-pb-[16px] tw-mb-[-16px] tw-px-[32px] tw-mx-[-32px]">
        {errors.global && (
          <Alert
            icon={<TriangleExclamationSolidV6 size="l" className="tw-text-semantic-red-120" />}
            variant="error"
            title={`An error occurred while ${next}grading`}
            text={errors.global}
          />
        )}

        {loadingTrackingNumbers && (
          <div className="tw-flex tw-justify-center tw-py-[24px]">
            <Loading />
          </div>
        )}

        {step === 1 && <Step1 accountProduct={accountProduct} newProduct={newProduct} next={next} />}

        {next === "up" && step === 2 && <Step2Up newProduct={newProduct} />}

        {next === "down" && step === 2 && requiresNumberDeletion && (
          <div className="tw-flex tw-flex-col tw-gap-[16px]">
            {/* @ts-expect-error props */}
            <Typography variant="headline3">
              Select tracking numbers to keep: {keepNumbers.length}/{newProduct?.details.trackingNumberMax}
            </Typography>
            <div className="tw-flex tw-flex-col">
              {trackingNumbers?.map((tn) => (
                <div
                  key={tn.id}
                  className="tw-bg-white first:tw-rounded-t-[8px] tw-border-b-0 last:tw-rounded-b-[8px] last:tw-border-b-[1px] tw-p-[16px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-flex tw-justify-between tw-items-center"
                >
                  {/* @ts-expect-error props */}
                  <Checkbox
                    checked={Boolean(keepNumbers.find((n) => n.id === tn.id))}
                    disabled={
                      !keepNumbers.find((n) => n.id === tn.id) &&
                      keepNumbers.length === newProduct?.details.trackingNumberMax
                    }
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (e.target.checked) {
                        setKeepNumbers((prev) => [...prev, tn]);
                      } else {
                        setKeepNumbers((prev) => prev.filter((n) => n.id !== tn.id));
                      }
                    }}
                    label={
                      <div className="tw-pl-[12px] tw-flex tw-flex-col tw-gap-[4px]">
                        {/* @ts-expect-error props */}
                        <Typography variant="subtitle-m">{tn.name}</Typography>
                        <span>{tn.number}</span>
                      </div>
                    }
                  />
                  {/* @ts-expect-error props */}
                  <Typography variant="subtitle-m" className="!tw-italic !tw-text-gray-50 !tw-font-normal">
                    {tn.users.length} Agents Assigned
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 3 && requiresNumberDeletion && (
          <div className="tw-flex tw-gap-[12px]">
            <ChangeNumberList numbers={deleteNumbers} variant="delete" />
            <ChangeNumberList numbers={keepNumbers} variant="keep" />
          </div>
        )}

        {step === 4 && (
          <div>
            Your plan change will be effective immediately
            {deleteNumbers.length > 0 &&
              ", and the tracking numbers you've confirmed in the previous step have been deleted"}
            .
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
        {step === 1 && (
          <>
            {next === "up" && (
              // @ts-expect-error props
              <TextButton size="medium" onClick={onClose}>
                Select Different Plan
              </TextButton>
            )}
            {next === "down" && (
              // @ts-expect-error props
              <Button size="medium" schema="secondary" onClick={onClose}>
                Keep My Subscription
              </Button>
            )}
            {/* @ts-expect-error props */}
            <Button
              type="submit"
              size="medium"
              schema={next === "up" ? "primary" : "tertiary"}
              isLoading={loading || loadingTrackingNumbers}
              onClick={async () => {
                if (next === "up") {
                  setLoading(true);
                  try {
                    await handleChangeSubscription();
                  } finally {
                    setLoading(false);
                  }
                } else {
                  if (!requiresNumberDeletion) {
                    await handleDowngradeWithDeletion();
                  } else {
                    setStep(2);
                  }
                }
              }}
            >
              {next === "up" ? `Switch to ${newProduct?.tier}` : "Downgrade"}
            </Button>
          </>
        )}

        {(step === 4 || (step === 2 && next === "up")) && (
          <>
            <span />
            {/* @ts-expect-error props */}
            <Button size="medium" onClick={onClose}>
              Done
            </Button>
          </>
        )}

        {(step === 2 || step === 3) && next === "down" && requiresNumberDeletion && (
          <>
            {/* @ts-expect-error props */}
            <Button size="medium" schema="tertiary" onClick={onClose}>
              Keep Current Plan
            </Button>
            {/* @ts-expect-error props */}
            <Button
              size="medium"
              disabled={
                (step === 2 && keepNumbers.length !== newProduct?.details.trackingNumberMax) || loading
              }
              schema="warning"
              isLoading={loading}
              onClick={async () => {
                if (step === 2) {
                  setStep(3);
                } else {
                  await handleDowngradeWithDeletion();
                }
              }}
            >
              {step === 3 ? "" : "Confirm"} Downgrade
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PhoneSystemChangeModal;
